import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import { store } from 'store/index'

import ReactSelect from 'pages/parts/ReactSelect'
import { SHOW_LOADER, HIDE_LOADER } from 'store/loader/actions'
import { httpOk, GET, POST } from 'helpers/api'
import { formatDate, formatMoney, returnMinDate, notify } from 'helpers/helpers'

let outputRowIdSeq = 0

function createOutputRow() {
	outputRowIdSeq += 1
	return {
		_id: outputRowIdSeq,
		name: '',
		product_id: null,
		element_id: null,
		balance: '',
		unit_name: '',
		product_type_name: '',
		quantity: '',
		cost_price: '',
		cost_share_percent: '',
		/** true: доля считается из себестоимости; false: себестоимость из доли */
		_deriveFromCost: false,
	}
}

function emptySourceRow() {
	return {
		name: '',
		product_id: null,
		element_id: null,
		balance: '',
		unit_name: '',
		product_type_name: '',
		quantity: '',
		cost_price: '',
	}
}

function defaultOutputs() {
	return [createOutputRow(), createOutputRow()]
}

/** Строка выхода участвует в расчётах и уходит в API только при выбранном продукте. */
function outputHasProduct(row) {
	return row?.product_id != null && row.product_id !== ''
}

function roundMoneyInt(value) {
	const n = Number(value)
	if (!Number.isFinite(n)) return 0
	return Math.round(n)
}

/** Знаков после запятой только для тела запроса к API. */
const OUTPUT_COST_API_DECIMALS = 2

function parseCostPriceInput(value) {
	const raw = String(value ?? '').replace(/\s/g, '').replace(',', '.')
	if (raw === '') return ''
	const n = Number(raw)
	if (!Number.isFinite(n)) return ''
	return String(Math.round(n))
}

function parseOutputUnitCostUi(value) {
	return roundMoneyInt(value)
}

/** Себестоимость за ед. (целое) для UI по целевой сумме строки. */
function unitCostFromLineTargetUi(lineTarget, rowQty) {
	const q = Number(rowQty) || 0
	if (q <= 0) return ''
	const lt = Math.round(Number(lineTarget) || 0)
	if (lt <= 0) return '0'
	return String(Math.round(lt / q))
}

/** Цена за ед. для API: (сумма сырья × доля / 100) / кол-во, 2 знака.
  * Считаем от неокруглённой суммы сырья — бэк сверяет строку с тем же значением. */
function outputUnitCostForApi(sourceRow, rowQty, sharePercent) {
	const st = sourceTotalAmountExact(sourceRow)
	const q = Number(rowQty) || 0
	const share = Number(sharePercent) || 0
	if (q <= 0 || share <= 0) return 0
	const m = 10 ** OUTPUT_COST_API_DECIMALS
	return Math.round((st * share / 100 / q) * m) / m
}

/** Знаков после запятой для доли % в строках выхода (UI и тело запроса). */
const OUTPUT_SHARE_DECIMALS = 4

function parseSharePercent(row) {
	const raw = String(row?.cost_share_percent ?? '').trim().replace(',', '.')
	if (raw === '') return 0
	const v = Number(raw)
	return Number.isFinite(v) ? v : 0
}

function isManualZeroCost(row) {
	const raw = String(row?.cost_price ?? '').trim().replace(',', '.')
	return row?._deriveFromCost === true && raw !== '' && parseOutputUnitCostUi(raw) === 0
}

/** Доля себестоимости для расчётов: только явное поле % (пустая доля ≠ доля по кол-ву). */
function effectiveCostSharePercent(row, _sourceRow) {
	if (isManualZeroCost(row)) return 0
	return parseSharePercent(row)
}

function outputRowIndicesWithQuantity(outputs) {
	return outputs
		.map((row, i) => {
			const q = Number(String(row?.quantity ?? '').replace(',', '.')) || 0
			return outputHasProduct(row) && q > 0 ? i : null
		})
		.filter((i) => i !== null)
}

function outputRowIndicesForCostAllocation(outputs) {
	return outputRowIndicesWithQuantity(outputs).filter((i) => !isManualZeroCost(outputs[i]))
}

/** Число доли % → строка для поля/ API без «хвоста» float. */
function formatOutputShareNumber(num) {
	const m = 10 ** OUTPUT_SHARE_DECIMALS
	const x = Math.round(Number(num) * m) / m
	let s = x.toFixed(OUTPUT_SHARE_DECIMALS)
	s = s.replace(/\.?0+$/, '')
	if (s === '' || s === '-0') return '0'
	return s
}

function formatOutputQuantityNumber(num) {
	const x = Math.round((Number(num) || 0) * 10000) / 10000
	let s = x.toFixed(4)
	s = s.replace(/\.?0+$/, '')
	if (s === '' || s === '-0') return '0'
	return s
}

function sumSharesPercent(outputs) {
	const s = outputs
		.filter((row) => outputHasProduct(row))
		.reduce((acc, row) => acc + parseSharePercent(row), 0)
	return Math.round(s * 100) / 100
}

/** Сколько % себестоимости ещё не разложено (100 − сумма долей). */
function outputsRemainderSharePercent(outputs) {
	const hasAnyShare = outputs.some((row) =>
		outputHasProduct(row) && String(row?.cost_share_percent ?? '').trim() !== '',
	)
	if (!hasAnyShare) return null
	return Math.round((100 - sumSharesPercent(outputs)) * 100) / 100
}

function outputRowAmount(row) {
	const q = Number(row.quantity) || 0
	const c = parseOutputUnitCostUi(row.cost_price)
	return Math.round(q * c)
}

function outputRowAmountByShare(sourceRow, row) {
	const share = effectiveCostSharePercent(row, sourceRow)
	return Math.round(sourceTotalAmount(sourceRow) * share / 100)
}

function outputRowDisplayAmount(sourceRow, row) {
	if (!sourceRow?.quantity) return outputRowAmount(row)
	const effShare = effectiveCostSharePercent(row, sourceRow)
	if (effShare > 0) return outputRowAmountByShare(sourceRow, row)
	if (String(row.cost_share_percent ?? '').trim() !== '') {
		return outputRowAmountByShare(sourceRow, row)
	}
	return outputRowAmount(row)
}

function sumOutputsAmount(outputs, sourceRow = null) {
	const s = outputs
		.filter((row) => outputHasProduct(row))
		.reduce((acc, row) => {
			const line = sourceRow ? outputRowDisplayAmount(sourceRow, row) : outputRowAmount(row)
			return acc + line
		}, 0)
	return Math.round(s)
}

function sumOutputsQuantity(outputs) {
	const s = outputs
		.filter((row) => outputHasProduct(row))
		.reduce((acc, row) => acc + (Number(row.quantity) || 0), 0)
	return Math.round(s * 10000) / 10000
}

/** Пустое поле количества выхода (для автозаполнения). */
function isOutputQuantityEmpty(row) {
	return String(row?.quantity ?? '').trim() === ''
}

/** Равномерно разбить остаток на count строк (сумма частей = remainder, до 4 знаков). */
function splitRemainderEvenly(remainder, count) {
	if (count <= 0) return []
	const parts = []
	let left = Math.round(remainder * 10000) / 10000
	for (let i = 0; i < count; i++) {
		const n = count - i
		const part = Math.round((left / n) * 10000) / 10000
		parts.push(part)
		left = Math.round((left - part) * 10000) / 10000
	}
	return parts
}

/** По кнопке «Рассчитать»:
 * — доли, которые уже заполнены, не трогаем;
 * — пустые доли заполняем остатком до 100% (пропорционально кол-ву);
 * — если все доли пустые — как раньше: 100% по количеству.
 */
function applyCostSharesFromOutputQuantities(outputs, sourceRow) {
	const sourceQty = parseSourceQuantity(sourceRow)
	if (!(sourceQty > 0)) return { outputs, didShare: false }

	const o = outputs.map((r) => ({ ...r }))

	for (let i = 0; i < o.length; i++) {
		if (!isManualZeroCost(o[i])) continue
		o[i] = {
			...o[i],
			cost_price: '0',
			cost_share_percent: '0',
			_deriveFromCost: true,
		}
	}

	const lockedIdx = []
	const freeIdx = []
	for (let i = 0; i < o.length; i++) {
		if (!outputHasProduct(o[i])) continue
		if (isManualZeroCost(o[i])) {
			lockedIdx.push(i)
			continue
		}
		const hasShare = String(o[i].cost_share_percent ?? '').trim() !== ''
		if (hasShare) lockedIdx.push(i)
		else freeIdx.push(i)
	}

	if (freeIdx.length === 0) {
		return { outputs: o, didShare: false }
	}

	const lockedSum = lockedIdx.reduce((acc, i) => acc + parseSharePercent(o[i]), 0)
	const remainingPool = Math.round(Math.max(0, 100 - lockedSum) * 10000) / 10000

	const totalAllocQty = freeIdx.reduce((acc, i) => {
		const q = Number(String(o[i].quantity ?? '').replace(',', '.')) || 0
		return acc + q
	}, 0)

	let didShare = false
	let sumHead = 0
	for (let j = 0; j < freeIdx.length; j++) {
		const i = freeIdx[j]
		const q = Number(String(o[i].quantity ?? '').replace(',', '.')) || 0
		const rawShare = totalAllocQty > 0
			? (q / totalAllocQty) * remainingPool
			: (remainingPool / freeIdx.length)
		const share = j === freeIdx.length - 1
			? Math.max(0, remainingPool - sumHead)
			: Math.round(rawShare * 10000) / 10000
		if (j !== freeIdx.length - 1) {
			sumHead = Math.round((sumHead + share) * 10000) / 10000
		}
		const shareStr = formatOutputShareNumber(share)
		if (String(o[i].cost_share_percent ?? '') !== shareStr || o[i]._deriveFromCost) {
			didShare = true
		}
		o[i] = {
			...o[i],
			cost_share_percent: shareStr,
			_deriveFromCost: false,
		}
	}

	return { outputs: o, didShare }
}

/** Чистый расчёт состояния по кнопке «Рассчитать» (кол-во + доли + sync цен). */
function tryCalculateOutputsUpdate(prev) {
	const sourceQty = parseSourceQuantity(prev.sourceRow)
	if (!(sourceQty > 0)) {
		return { ok: false, notifyKey: 'breakdown_fill_source_qty' }
	}

	const sumOut = sumOutputsQuantity(prev.outputs)
	const remainder = Math.round((sourceQty - sumOut) * 10000) / 10000

	if (remainder < -0.0001) {
		return { ok: false, notifyKey: 'breakdown_calc_overfilled_qty' }
	}

	let outputs = prev.outputs.map((r) => ({ ...r }))
	let didQty = false

	if (remainder > 0.0001) {
		const emptyIdx = prev.outputs
			.map((row, i) =>
				outputHasProduct(row) && isOutputQuantityEmpty(row) ? i : null,
			)
			.filter((i) => i !== null)

		if (emptyIdx.length > 0) {
			const parts = splitRemainderEvenly(remainder, emptyIdx.length)
			emptyIdx.forEach((idx, j) => {
				outputs[idx] = {
					...outputs[idx],
					quantity: formatOutputQuantityNumber(parts[j]),
				}
			})
			didQty = true
		} else {
			let last = -1
			for (let i = outputs.length - 1; i >= 0; i--) {
				if (outputHasProduct(outputs[i])) {
					last = i
					break
				}
			}
			if (last < 0) {
				return { ok: false, notifyKey: 'breakdown_calc_nothing_to_distribute' }
			}
			const raw = String(outputs[last].quantity ?? '').trim().replace(',', '.')
			const q = raw === '' ? 0 : Number(raw)
			const cur = Number.isFinite(q) ? q : 0
			const next = Math.round((cur + remainder) * 10000) / 10000
			outputs[last] = {
				...outputs[last],
				quantity: formatOutputQuantityNumber(next),
			}
			didQty = true
		}
	}

	const qtyShareResult = applyCostSharesFromOutputQuantities(outputs, prev.sourceRow)
	outputs = qtyShareResult.outputs

	const nextState = syncOutputDerivedFields({ ...prev, outputs }, { balanceMoneyTotals: true })

	if (!didQty && !qtyShareResult.didShare) {
		return { ok: false, notifyKey: 'breakdown_calc_nothing_to_distribute' }
	}

	return { ok: true, state: nextState }
}

function parseSourceQuantity(sourceRow) {
	const raw = String(sourceRow?.quantity ?? '').trim().replace(',', '.')
	if (raw === '') return 0
	const q = Number(raw)
	return Number.isFinite(q) ? q : 0
}

/** Сколько количества ещё не внесено в outputs (исход − сумма выходов). */
function outputsRemainderQuantity(sourceRow, outputs) {
	const sq = parseSourceQuantity(sourceRow)
	if (!(sq > 0)) return null
	const allocated = sumOutputsQuantity(outputs)
	return Math.round(Math.max(0, sq - allocated) * 10000) / 10000
}

/** Сколько денег по долям ещё не «разложено» по выходам (сумма исхода − суммы строк выхода по таблице). */
function outputsRemainderAmount(sourceRow, outputs) {
	const sq = parseSourceQuantity(sourceRow)
	if (!(sq > 0)) return null
	const st = sourceTotalAmount(sourceRow)
	const allocated = sumOutputsAmount(outputs, sourceRow)
	return Math.max(0, Math.round(st - allocated))
}

/** Точная сумма сырья (кол-во × цена) — без округления до целых сумов. */
function sourceTotalAmountExact(sourceRow) {
	const q = Number(sourceRow?.quantity) || 0
	const c = roundMoneyInt(sourceRow?.cost_price)
	return q * c
}

/** Сумма сырья для UI и распределения по строкам — целые сумы. */
function sourceTotalAmount(sourceRow) {
	return Math.round(sourceTotalAmountExact(sourceRow))
}

/**
 * Целевые суммы строк по долям %; последняя строка с долей > 0
 * получает остаток, чтобы сумма строк = сумме сырья.
 */
function computeOutputLineTargetsFromShares(sourceRow, outputs) {
	const S = sourceTotalAmount(sourceRow)
	const targets = {}
	const indices = outputRowIndicesForCostAllocation(outputs)

	if (indices.length === 0) return targets

	let allocated = 0
	for (let j = 0; j < indices.length - 1; j++) {
		const i = indices[j]
		const t = Math.round(S * effectiveCostSharePercent(outputs[i], sourceRow) / 100)
		targets[i] = t
		allocated += t
	}
	targets[indices[indices.length - 1]] = S - allocated

	for (let i = 0; i < outputs.length; i++) {
		if (!outputHasProduct(outputs[i])) continue
		if (isManualZeroCost(outputs[i])) {
			targets[i] = 0
			continue
		}
		if (targets[i] === undefined) targets[i] = 0
	}

	return targets
}

/** Лучшая целая цена за ед., чтобы round(q×c) была ближе к целевой сумме строки. */
function bestUnitCostForLineTarget(lineTarget, rowQty) {
	const q = Number(rowQty) || 0
	const T = Math.round(Number(lineTarget) || 0)
	if (q <= 0 || T <= 0) return 0
	const base = Math.floor(T / q)
	let best = base
	let bestErr = Math.abs(Math.round(q * best) - T)
	for (const c of [base, base + 1]) {
		if (c < 0) continue
		const err = Math.abs(Math.round(q * c) - T)
		if (err < bestErr) {
			bestErr = err
			best = c
		}
	}
	return best
}

/** Доля % из себестоимости за ед. и количества (округление 2 знака). */
function sharePercentFromUnitCost(sourceRow, rowQty, unitCost) {
	const st = sourceTotalAmount(sourceRow)
	const q = Number(rowQty) || 0
	if (st <= 0 || q <= 0) return null
	const rowTotal = Math.round(q * parseOutputUnitCostUi(unitCost))
	const pct = (rowTotal / st) * 100
	return Math.round(pct * 100) / 100
}

/** Сумма строки выхода в деньгах: round(q × цена). */
function outputLineMoneyTotal(row) {
	const q = Number(String(row.quantity ?? '').replace(',', '.')) || 0
	const c = parseOutputUnitCostUi(row.cost_price)
	return Math.round(q * c)
}

/** Строка выхода может участвовать в подгонке суммы (не нулевая доля %). */
function outputRowEligibleForMoneyReconcile(row, sourceRow) {
	if (!outputHasProduct(row)) return false
	const q = Number(String(row.quantity ?? '').replace(',', '.')) || 0
	if (q <= 0) return false
	return effectiveCostSharePercent(row, sourceRow) > 0
}

/**
 * Подгоняет целые себестоимости за ед., чтобы sum(round(q×c)) = сумма сырья
 * (остаток от «доля → цена за ед.» при несовпадении q и округления).
 */
function reconcileOutputMoneyTotals(sourceRow, outputs) {
	const S = sourceTotalAmount(sourceRow)
	if (S <= 0) return outputs

	const o = outputs.map((r) => ({ ...r }))
	const lineTargets = computeOutputLineTargetsFromShares(sourceRow, o)

	// Сразу ставим лучшую целую цену под целевую сумму строки — без цикла ±1
	// (раньше мог «разгонять» себестоимость до значений вроде 75000099).
	for (let i = 0; i < o.length; i++) {
		if (!outputRowEligibleForMoneyReconcile(o[i], sourceRow)) continue
		if (o[i]._deriveFromCost && !isManualZeroCost(o[i])) continue
		const q = Number(String(o[i].quantity ?? '').replace(',', '.')) || 0
		const targetLine = lineTargets[i] != null
			? lineTargets[i]
			: Math.round(S * effectiveCostSharePercent(o[i], sourceRow) / 100)
		const bestC = bestUnitCostForLineTarget(targetLine, q)
		const curC = parseOutputUnitCostUi(o[i].cost_price)
		if (bestC !== curC) {
			o[i] = { ...o[i], cost_price: String(bestC), _deriveFromCost: false }
		}
	}

	let diff = S - o.reduce(
		(acc, row) => acc + (outputHasProduct(row) ? outputLineMoneyTotal(row) : 0),
		0,
	)
	if (diff === 0) return o

	// До lag ±1 только по строкам без ручного ввода, с разумным лимитом шагов.
	const maxSteps = Math.min(Math.abs(diff) + 2, 5000)
	let guard = 0
	while (diff !== 0 && guard < maxSteps) {
		guard += 1
		const sign = diff > 0 ? 1 : -1
		let progressed = false
		for (let i = o.length - 1; i >= 0; i--) {
			if (!outputRowEligibleForMoneyReconcile(o[i], sourceRow)) continue
			if (o[i]._deriveFromCost && !isManualZeroCost(o[i])) continue
			const q = Number(String(o[i].quantity ?? '').replace(',', '.')) || 0
			const c = parseOutputUnitCostUi(o[i].cost_price)
			const nextC = c + sign
			if (nextC < 0) continue
			const curAmt = Math.round(q * c)
			const nextAmt = Math.round(q * nextC)
			const delta = nextAmt - curAmt
			if (delta === 0) continue
			if (diff > 0 && delta > 0 && delta <= diff) {
				o[i] = { ...o[i], cost_price: String(nextC), _deriveFromCost: false }
				diff -= delta
				progressed = true
				break
			}
			if (diff < 0 && delta < 0 && delta >= diff) {
				o[i] = { ...o[i], cost_price: String(nextC), _deriveFromCost: false }
				diff -= delta
				progressed = true
				break
			}
		}
		if (!progressed) break
	}

	return o
}

/**
 * После выравнивания денег по строкам — доли % из фактических сумм строк,
 * чтобы при следующем pass1 unitCostFromShare не ломал баланс.
 */
function recomputeSharesFromBalancedLineTotals(sourceRow, outputs) {
	const S = sourceTotalAmount(sourceRow)
	if (S <= 0) return outputs

	const idxList = outputRowIndicesForCostAllocation(outputs)
	if (idxList.length === 0) return outputs

	const amounts = idxList.map((i) => outputLineMoneyTotal(outputs[i]))
	const sumA = amounts.reduce((a, b) => a + b, 0)
	if (sumA !== S) return outputs

	const o = outputs.map((r) => ({ ...r }))
	for (let i = 0; i < o.length; i++) {
		if (isManualZeroCost(o[i])) {
			o[i] = { ...o[i], cost_price: '0', cost_share_percent: '0', _deriveFromCost: true }
		}
	}
	if (idxList.length === 1) {
		const ii = idxList[0]
		o[ii] = { ...o[ii], cost_share_percent: '100', _deriveFromCost: false }
		return o
	}

	const m = 10 ** OUTPUT_SHARE_DECIMALS
	const headRounded = []
	let sumHead = 0
	for (let j = 0; j < idxList.length - 1; j++) {
		const raw = (amounts[j] * 100) / S
		const p = Math.round(raw * m) / m
		headRounded.push(p)
		sumHead += p
	}
	const lastP = Math.round((100 - sumHead) * m) / m
	for (let j = 0; j < idxList.length - 1; j++) {
		const ii = idxList[j]
		o[ii] = {
			...o[ii],
			cost_share_percent: formatOutputShareNumber(headRounded[j]),
			_deriveFromCost: false,
		}
	}
	const lastI = idxList[idxList.length - 1]
	o[lastI] = {
		...o[lastI],
		cost_share_percent: formatOutputShareNumber(lastP),
		_deriveFromCost: false,
	}
	return o
}

/**
 * Синхронизация: из доли → цена, из цены → доля.
 * balanceMoneyTotals: подогнать суммы строк к сумме сырья (Enter / Рассчитать / сохранение),
 * не при каждом символе в поле себестоимости.
 */
function syncOutputDerivedFields(state, options = {}) {
	const balanceMoneyTotals = options.balanceMoneyTotals === true
	const sourceRow = state.sourceRow
	let outputs = state.outputs.map((r) => ({ ...r }))

	for (let i = 0; i < outputs.length; i++) {
		const row = outputs[i]
		if (!outputHasProduct(row)) continue
		const q = Number(String(row.quantity ?? '').replace(',', '.')) || 0
		const hasShare = String(row.cost_share_percent ?? '').trim() !== ''
		const hasCost = String(row.cost_price ?? '').trim() !== ''
		if (!row._deriveFromCost && !hasShare && hasCost && q > 0) {
			outputs[i] = { ...outputs[i], _deriveFromCost: true }
		}
	}

	for (let i = 0; i < outputs.length; i++) {
		const row = outputs[i]
		if (!outputHasProduct(row)) continue
		if (isManualZeroCost(row)) {
			if (String(row.cost_share_percent ?? '') !== '0' || String(row.cost_price ?? '') !== '0') {
				outputs[i] = { ...row, cost_price: '0', cost_share_percent: '0', _deriveFromCost: true }
			}
		}
	}

	if (balanceMoneyTotals && outputs.some((row) => isManualZeroCost(row))) {
		outputs = applyCostSharesFromOutputQuantities(outputs, sourceRow).outputs
	}

	// Пасс 1: строки «из доли» — пересчитать себестоимость за ед. (целые) по целевым суммам строк
	const lineTargets = computeOutputLineTargetsFromShares(sourceRow, outputs)
	for (let i = 0; i < outputs.length; i++) {
		const row = outputs[i]
		if (row._deriveFromCost) continue
		if (!outputHasProduct(row)) continue
		const q = Number(String(row.quantity ?? '').replace(',', '.')) || 0
		const sh = effectiveCostSharePercent(row, sourceRow)
		if (q <= 0) {
			outputs[i] = { ...row, cost_price: '' }
			continue
		}
		if (sh <= 0 && !String(row.cost_share_percent ?? '').trim()) {
			outputs[i] = { ...row, cost_price: '' }
			continue
		}
		if (sh <= 0) {
			outputs[i] = { ...row, cost_price: '0' }
		} else {
			const lt = lineTargets[i] ?? Math.round(sourceTotalAmount(sourceRow) * sh / 100)
			outputs[i] = {
				...row,
				cost_share_percent: formatOutputShareNumber(sh),
				cost_price: unitCostFromLineTargetUi(lt, q),
				_deriveFromCost: false,
			}
		}
	}

	const fixedShareSum = outputs.reduce(
		(acc, row) =>
			acc + ((row._deriveFromCost || !outputHasProduct(row)) ? 0 : parseSharePercent(row)),
		0,
	)
	let remaining = Math.round((100 - fixedShareSum) * 100) / 100
	if (remaining < 0) remaining = 0

	// Пасс 2: строки «из цены» — пересчитать долю % (по очереди, в пределах оставшихся %)
	for (let i = 0; i < outputs.length; i++) {
		const row = outputs[i]
		if (!row._deriveFromCost || !outputHasProduct(row)) continue
		const q = Number(String(row.quantity ?? '').replace(',', '.')) || 0
		const costStr = String(row.cost_price ?? '').trim()
		if (q <= 0 || costStr === '') {
			outputs[i] = { ...row, cost_share_percent: '', cost_price: '' }
			continue
		}
		const c = parseOutputUnitCostUi(row.cost_price)
		const st = sourceTotalAmount(sourceRow)
		if (st <= 0) {
			outputs[i] = { ...row, cost_share_percent: '', cost_price: '' }
			continue
		}
		const raw = sharePercentFromUnitCost(sourceRow, q, c)
		if (raw == null) {
			outputs[i] = { ...row, cost_share_percent: '', cost_price: '' }
			continue
		}
		const share = Math.round(Math.min(raw, Math.max(0, remaining)) * 100) / 100
		remaining = Math.round((remaining - share) * 100) / 100
		outputs[i] = {
			...row,
			cost_share_percent: String(share),
		}
	}

	let out = outputs
	if (balanceMoneyTotals) {
		out = reconcileOutputMoneyTotals(sourceRow, out)
		const sumLines = out.reduce(
			(acc, row) => acc + (outputHasProduct(row) ? outputLineMoneyTotal(row) : 0),
			0,
		)
		if (sumLines === sourceTotalAmount(sourceRow)) {
			out = recomputeSharesFromBalancedLineTotals(sourceRow, out)
		}
	}

	return { ...state, outputs: out }
}

function productDisplayName(option) {
	if (!option) return ''
	const n = option.name ?? option.title ?? option.product_name
	if (n != null && String(n).trim() !== '') return String(n)
	const id = option.id ?? option.element_id
	return id != null ? String(id) : ''
}

function CreateUpdate() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const reduxAccount = useSelector(state => state.account)

	const scrollRef = useRef(null)

	const [data, setData] = useState({
		from_wms_id: reduxAccount.wms_id ?? null,
		to_wms_id: reduxAccount.wms_id ?? null,
		created_at: formatDate(new Date(), 'yyyy-MM-dd'),
		note: '',
		activeIndex: 'source',
		sourceRow: emptySourceRow(),
		outputs: defaultOutputs(),
	})
	const [wms, setWms] = useState([])
	const [items, setItems] = useState([])
	const [elementsDuplicate, setElementsDuplicate] = useState([])
	const [addQuantity, setAddQuantity] = useState(1)

	async function createUpdate() {
		console.log(JSON.stringify(data))

		if (!data.from_wms_id) {
			notify(t('check_filled_inputs'))
			return
		}
		if (!data.to_wms_id) {
			notify(t('breakdown_select_to_wms'))
			return
		}
		if (!data.sourceRow.product_id) {
			notify(t('check_filled_inputs'))
			return
		}
		const form = syncOutputDerivedFields(data, { balanceMoneyTotals: true })
		const qty = Number(form.sourceRow.quantity)
		const cost = roundMoneyInt(form.sourceRow.cost_price)
		if (!qty || qty <= 0 || Number.isNaN(qty)) {
			notify(t('breakdown_fill_source_qty'))
			return
		}
		if (Number.isNaN(cost) || cost < 0) {
			notify(t('check_filled_inputs'))
			return
		}
		const filledOutputs = form.outputs.filter((row) => outputHasProduct(row))
		if (filledOutputs.length === 0) {
			notify(t('breakdown_min_one_output'))
			return
		}
		const outputsPayload = []
		for (let i = 0; i < filledOutputs.length; i++) {
			const row = filledOutputs[i]
			const q = Number(row.quantity)
			const sh = parseSharePercent(row)
			const rowCostApi = sh > 0 ? outputUnitCostForApi(form.sourceRow, q, sh) : 0
			if (!q || q <= 0 || Number.isNaN(q)) {
				notify(t('check_filled_inputs'))
				return
			}
			if (Number.isNaN(rowCostApi) || rowCostApi < 0) {
				notify(t('check_filled_inputs'))
				return
			}
			if (!String(row.cost_share_percent ?? '').trim()) {
				notify(t('check_filled_inputs'))
				return
			}
			if (Number.isNaN(sh) || sh < 0 || sh > 100) {
				notify(t('check_filled_inputs'))
				return
			}
			outputsPayload.push({
				product_id: row.product_id,
				quantity: q,
				cost_price: rowCostApi,
				cost_share_percent: sh,
			})
		}

		const sourceQty = qty
		const outputsQty = sumOutputsQuantity(form.outputs)
		if (Math.abs(outputsQty - sourceQty) > 0.0001) {
			notify(t('breakdown_outputs_qty_must_equal_source'))
			return
		}

		const sharesSum = sumSharesPercent(form.outputs)
		if (Math.abs(sharesSum - 100) > 0.02) {
			notify(t('sum_cost_share_must_100'))
			return
		}

		const sourceTotal = sourceTotalAmount(form.sourceRow)
		// Сумма по долям % (как в таблице); round по строкам при целых сумах даёт ±1.
		const outputsTotal = sumOutputsAmount(form.outputs, form.sourceRow)
		if (Math.abs(outputsTotal - sourceTotal) > 1) {
			notify(t('breakdown_outputs_cost_must_match_source'))
			return
		}

		setData(form)

		const created_at = `${data.created_at} 12:00:00`
		const body = {
			from_wms_id: data.from_wms_id,
			to_wms_id: data.to_wms_id,
			created_at,
			note: data.note || null,
			product: {
				product_id: data.sourceRow.product_id,
				quantity: qty,
				cost_price: cost,
				outputs: outputsPayload,
			},
		}
		const response = await POST('/documents-breakdown', body, { loader: true })
		if (httpOk(response)) navigate('/documents-breakdown')
	}

	function sourceAmount() {
		return sourceTotalAmount(data.sourceRow)
	}

	function handleCalculateOutputs() {
		const r = tryCalculateOutputsUpdate(data)
		if (!r.ok) {
			if (r.notifyKey) notify(t(r.notifyKey))
			return
		}
		setData(r.state)
	}

	function closeMenu() {
		setTimeout(() => {
			if (!items.length) return
			const elementsCopy = [...items]
			for (let i = 0; i < elementsCopy.length; i++) {
				elementsCopy[i].selected = false
			}
			elementsCopy[0].selected = true
			setItems(elementsCopy)
		}, 100)
	}

	function resetProductPickList() {
		setItems(elementsDuplicate.map(p => ({ ...p, selected: false })))
	}

	async function handleChangeWms(option) {
		setData({
			...data,
			activeIndex: 'source',
			from_wms_id: option ? option.id : null,
			sourceRow: emptySourceRow(),
			outputs: defaultOutputs(),
		})

		await loadProducts(option.id, true)
	}

	function handleSelectSearch(value) {
		if (!value) return
		const q = value.trim()
		if (!q) return
		const qLower = q.toLowerCase()
		const filteredItems = elementsDuplicate
			.map(p => ({ ...p, selected: false }))
			.filter(item => productDisplayName(item).toLowerCase().includes(qLower))
		if (filteredItems[0]) filteredItems[0].selected = true
		setItems(filteredItems)
	}

	function handleTableKeyDown(event) {
		event.stopPropagation()
		const currentIndex = items.findIndex(el => el.selected)
		if (currentIndex < 0) return
		const elementsCopy = [...items]
		elementsCopy[currentIndex].selected = false
		switch (event.key) {
			case 'ArrowUp':
				if (currentIndex === 0) {
					elementsCopy[items.length - 1].selected = true
				} else {
					elementsCopy[currentIndex - 1].selected = true
				}
				if (scrollRef.current) {
					scrollRef.current.scrollTop = (currentIndex * 29.6) - 59.2
				}
				break
			case 'ArrowDown':
				if (currentIndex === items.length - 1) {
					elementsCopy[0].selected = true
				} else {
					elementsCopy[currentIndex + 1].selected = true
				}
				if (scrollRef.current) {
					scrollRef.current.scrollTop = currentIndex * 29.6
				}
				break
			default:
				break
		}
		setItems(elementsCopy)
	}

	function handleSelectSourceProduct(option) {
		if (!option) return
		const pid = option.id ?? option.element_id
		const label = productDisplayName(option)
		setData((prev) =>
			syncOutputDerivedFields({
				...prev,
				activeIndex: 'source',
				sourceRow: {
					...prev.sourceRow,
					product_id: pid,
					element_id: pid,
					name: label,
					balance: option.balance,
					unit_name: option.unit_name,
					cost_price: option.cost_price != null
						? String(roundMoneyInt(option.cost_price))
						: '',
					product_type_name: option.product_type_name,
				},
			}),
		)
		resetProductPickList()
	}

	function handleSelectOutputProduct(option, index) {
		if (!option) return
		const pid = option.id ?? option.element_id
		const label = productDisplayName(option)
		setData((prev) =>
			syncOutputDerivedFields({
				...prev,
				activeIndex: index,
				outputs: prev.outputs.map((row, i) => {
					if (i !== index) return row
					return {
						...row,
						product_id: pid,
						element_id: pid,
						name: label,
						balance: option.balance,
						unit_name: option.unit_name,
						product_type_name: option.product_type_name,
						cost_share_percent: '',
						cost_price: '',
						_deriveFromCost: false,
					}
				}),
			}),
		)
		resetProductPickList()
	}

	function handleSourceQuantityChange(e) {
		const v = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '')
		setData((prev) =>
			syncOutputDerivedFields({
				...prev,
				sourceRow: { ...prev.sourceRow, quantity: v },
			}),
		)
	}

	function handleSourceCostChange(e) {
		const v = parseCostPriceInput(e.target.value)
		setData((prev) =>
			syncOutputDerivedFields({
				...prev,
				sourceRow: { ...prev.sourceRow, cost_price: v },
			}),
		)
	}

	function patchOutputQuantity(index, value) {
		const v = value.replace(/,/g, '.').replace(/[^0-9.]/g, '')
		setData((prev) => {
			const outputs = prev.outputs.map((row, i) => (
				i === index ? { ...row, quantity: v } : row
			))
			return syncOutputDerivedFields({ ...prev, outputs }, { balanceMoneyTotals: false })
		})
	}

	function patchOutputCostPrice(index, rawValue) {
		const v = parseCostPriceInput(rawValue)
		setData((prev) => {
			if (!outputHasProduct(prev.outputs[index])) return prev
			const outputs = prev.outputs.map((row, i) =>
				i === index ? { ...row, cost_price: v, _deriveFromCost: true } : row,
			)
			return syncOutputDerivedFields({ ...prev, outputs }, { balanceMoneyTotals: false })
		})
	}

	function patchOutputSharePercent(index, rawValue) {
		const v = String(rawValue ?? '')
			.replace(/[^0-9.,]/g, '')
			.replace(',', '.')
		setData((prev) => {
			if (!outputHasProduct(prev.outputs[index])) return prev
			const outputs = prev.outputs.map((row, i) =>
				i === index
					? { ...row, cost_share_percent: v, _deriveFromCost: false }
					: row,
			)
			return syncOutputDerivedFields({ ...prev, outputs }, { balanceMoneyTotals: false })
		})
	}

	function handleOutputRowKeyDown(e, index, field) {
		if (e.key !== 'Enter') return
		e.preventDefault()
		if (field === 'quantity' || field === 'cost' || field === 'share') {
			setData((prev) => syncOutputDerivedFields(prev, { balanceMoneyTotals: true }))
		}
	}

	function addOutputRowsFromInput(e) {
		e.preventDefault()
		e.stopPropagation()
		const n = Math.max(1, parseInt(addQuantity, 10) || 1)
		setData((prev) => ({
			...prev,
			outputs: [
				...prev.outputs,
				...Array.from({ length: n }, () => createOutputRow()),
			],
		}))
		setAddQuantity(1)
	}

	function removeOutputRow(index) {
		setData((prev) => {
			if (prev.outputs.length <= 1) {
				notify(t('breakdown_min_one_output'))
				return prev
			}
			return {
				...prev,
				outputs: prev.outputs.filter((_, i) => i !== index),
			}
		})
	}

	async function loadProducts(fromWmsId, useLoader = true) {
		if (!fromWmsId) {
			setElementsDuplicate([])
			setItems([])
			return
		}

		const filterUrl = `/documents-breakdown-products/${fromWmsId}`
		const response = await GET(filterUrl, {}, { loader: useLoader })
		if (httpOk(response)) {
			const list = (response.data || []).map(p => ({ ...p, selected: false }))
			setElementsDuplicate(list)
			setItems(list)
		}
	}

	async function getData() {
		store.dispatch(SHOW_LOADER())
		try {
			const response = await GET('/helper/wms', {}, { loader: false })
			if (httpOk(response)) setWms(response.data)
			await loadProducts(data.from_wms_id, false)
		} finally {
			store.dispatch(HIDE_LOADER())
		}
	}

	useEffect(() => {
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const outputRemainderQty = outputsRemainderQuantity(data.sourceRow, data.outputs)
	const outputRemainderAmt = outputsRemainderAmount(data.sourceRow, data.outputs)
	const outputRemainderShare = outputsRemainderSharePercent(data.outputs)
	const outputsShareSum = sumSharesPercent(data.outputs)

	return (
		<>
			{/* {console.log(JSON.stringify(data))} */}
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">{t('documents_breakdown')}</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{t('create')}</h6>
					</div>
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<div className="row">
						<div className="col-md-2">
							<div className="form-group">
								<label>{t('date')}<span className="required-mark">*</span></label>
								<div className="calendar-input">
									<DatePicker
										showMonthDropdown
										showYearDropdown
										dropdownMode="select"
										yearDropdownItemNumber={2}
										className="form-control"
										dateFormat="dd.MM.yyyy"
										selected={data.created_at ? new Date(data.created_at) : ''}
										onChange={(date) => setData({ ...data, created_at: formatDate(date, 'yyyy-MM-dd') })}
										minDate={returnMinDate(new Date(), 20)}
										maxDate={new Date()} />
									<i className="uil uil-calendar-alt"></i>
								</div>
							</div>
						</div>
						<div className="col-md-2">
							<div className="form-group">
								<label>{t('from_warehouse')}<span className="required-mark">*</span></label>
								<Select
									options={wms}
									value={wms.find((o) => o.id === data.from_wms_id) || null}
									onChange={(option) => handleChangeWms(option)}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>
						</div>
						<div className="col-md-2">
							<div className="form-group">
								<label>{t('to_warehouse')}<span className="required-mark">*</span></label>
								<Select
									options={wms}
									value={wms.find((o) => o.id === data.to_wms_id) || null}
									onChange={(option) => setData({ ...data, to_wms_id: option ? option.id : null, })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>
						</div>
						<div className="col-md-2">
							<div className="form-group">
								<label>{t('note')}</label>
								<input type="text" className="form-control" value={data.note}
									onChange={(e) => setData({ ...data, note: e.target.value })} />
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<h5 className="mb-3">{t('breakdown_source_product')}</h5>
					<div style={{ overflow: 'auto' }}>
						<table className="table table-bordered mb-0" style={{ overflow: 'unset' }}>
							<thead>
								<tr>
									<th style={{ minWidth: '33px' }} className="text-center">#</th>
									<th style={{ width: '40%' }}>{t('name2')}</th>
									<th style={{ width: '10%' }}>{t('type')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('balance2')}</th>
									<th style={{ width: '5%' }} className="text-nowrap">{t('unit')}</th>
									<th  style={{ width: '10%' }}></th>
									<th style={{ width: '5%' }} className="text-end">{t('quantity')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('cost_price')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('amount')}</th>
									<th style={{ minWidth: '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td
										className="text-center"
										style={{ background: data.activeIndex === 'source' ? '#c1c1c1' : '' }}
										onClick={() => setData(prev => ({ ...prev, activeIndex: 'source' }))}>
										1
									</td>
									<td onClick={() => setData(prev => ({ ...prev, activeIndex: 'source' }))}>
										<ReactSelect
											search={handleSelectSearch}
											value={data.sourceRow.name}
											index={0}
											uniqueId="brk-src"
											scrollRef={scrollRef}
											options={items}
											isBarcodeSearchEnabled={false}
											onKeyUp={(e) => {
												if (e.keyCode === 40 || e.keyCode === 38) {
													handleTableKeyDown(e)
												}
											}}
											onClose={() => closeMenu()}
											selectItem={(option) => {
												if (option) handleSelectSourceProduct(option)
											}}
											table={
												<table className="table mb-0">
													<tbody>
														{items.map((item2, index2) => (
															<tr
																className={'cursor ' + ((item2.selected || (item2.id ?? item2.element_id) === data.sourceRow.element_id) ? 'selected' : '')}
																tabIndex={-1}
																key={item2.id ?? item2.element_id ?? index2}
																onMouseDown={(e) => e.preventDefault()}
																onClick={(e) => {
																	e.stopPropagation()
																	handleSelectSourceProduct(item2)
																}}
																onKeyDown={(e) => handleTableKeyDown(e)}>
																<td style={{ width: '50%' }}>{productDisplayName(item2)}</td>
																<td style={{ width: '25%' }} className="text-end text-nowrap">{item2.product_type_name}</td>
																<td style={{ width: '25%' }} className="text-end text-nowrap">{formatMoney(item2.cost_price)}</td>
															</tr>
														))}
													</tbody>
												</table>
											}
										/>
									</td>
									<td onClick={() => setData(prev => ({ ...prev, activeIndex: 'source' }))}>
										{data.sourceRow.product_type_name}
									</td>
									<td className="text-end" onClick={() => setData(prev => ({ ...prev, activeIndex: 'source' }))}>
										{formatMoney(data.sourceRow.balance)}
									</td>
									<td onClick={() => setData(prev => ({ ...prev, activeIndex: 'source' }))}>
										{data.sourceRow.unit_name}
									</td>
									<td></td>
									<td className="td-input" onClick={() => setData(prev => ({ ...prev, activeIndex: 'source' }))}>
										<input
											type="text"
											className="form-control text-end"
											placeholder="0"
											value={data.sourceRow.quantity ?? ''}
											onChange={handleSourceQuantityChange} />
									</td>
									<td className="td-input" onClick={() => setData(prev => ({ ...prev, activeIndex: 'source' }))}>
										<input
											type="text"
											className="form-control text-end"
											placeholder="0"
											value={data.sourceRow.cost_price ?? ''}
											onChange={handleSourceCostChange} />
									</td>
									<td className="text-end text-nowrap" onClick={() => setData(prev => ({ ...prev, activeIndex: 'source' }))}>
										{formatMoney(sourceAmount())}
									</td>
									<td></td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
						<h5 className="mb-0">{t('breakdown_outputs')}</h5>
						<button
							type="button"
							className="btn btn-sm btn-outline-primary"
							disabled={!(parseSourceQuantity(data.sourceRow) > 0)}
							onClick={handleCalculateOutputs}>
							{t('breakdown_calculate')}
						</button>
					</div>
					<div style={{ overflow: 'auto' }}>
						<table className="table table-bordered mb-0" style={{ overflow: 'unset' }}>
							<thead>
								<tr>
									<th style={{ minWidth: '33px' }} className="text-center">#</th>
									<th style={{ width: '39%' }}>{t('name2')}</th>
									<th style={{ width: '8%' }}>{t('type')}</th>
									<th style={{ width: '8%' }} className="text-end">{t('balance2')}</th>
									<th style={{ width: '5%' }} className="text-nowrap">{t('unit')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('quantity')}</th>
									<th style={{ width: '10%' }} className="text-end text-nowrap">{t('cost_share_percent')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('cost_price')}</th>
									<th style={{ width: '10%' }} className="text-end">{t('amount')}</th>
									<th style={{ minWidth: '33px' }} className="text-end"></th>
								</tr>
							</thead>
							<tbody>
								{data.outputs.map((row, index) => (
									<tr key={row._id ?? index}>
										<td
											className="text-center"
											style={{ background: data.activeIndex === index ? '#c1c1c1' : '' }}
											onClick={() => setData(prev => ({ ...prev, activeIndex: index }))}>
											{index + 1}
										</td>
										<td onClick={() => setData(prev => ({ ...prev, activeIndex: index }))}>
											<ReactSelect
												search={handleSelectSearch}
												value={row.name}
												index={0}
												uniqueId={`brk-out-${index}`}
												scrollRef={scrollRef}
												options={items}
												isBarcodeSearchEnabled={false}
												onKeyUp={(e) => {
													if (e.keyCode === 40 || e.keyCode === 38) {
														handleTableKeyDown(e)
													}
												}}
												onClose={() => closeMenu()}
												selectItem={(option) => {
													if (option) handleSelectOutputProduct(option, index)
												}}
												table={
													<table className="table mb-0">
														<tbody>
															{items.map((item2, index2) => (
																<tr
																	className={'cursor ' + ((item2.selected || (item2.id ?? item2.element_id) === row.element_id) ? 'selected' : '')}
																	tabIndex={-1}
																	key={item2.id ?? item2.element_id ?? index2}
																	onMouseDown={(e) => e.preventDefault()}
																	onClick={(e) => {
																		e.stopPropagation()
																		handleSelectOutputProduct(item2, index)
																	}}
																	onKeyDown={(e) => handleTableKeyDown(e)}>
																	<td style={{ width: '50%' }}>{productDisplayName(item2)}</td>
																	<td style={{ width: '25%' }} className="text-end text-nowrap">{item2.product_type_name}</td>
																	<td style={{ width: '25%' }} className="text-end text-nowrap">{formatMoney(item2.cost_price)}</td>
																</tr>
															))}
														</tbody>
													</table>
												}
											/>
										</td>
										<td onClick={() => setData(prev => ({ ...prev, activeIndex: index }))}>{row.product_type_name}</td>
										<td className="text-end" onClick={() => setData(prev => ({ ...prev, activeIndex: index }))}>
											{formatMoney(row.balance)}
										</td>
										<td onClick={() => setData(prev => ({ ...prev, activeIndex: index }))}>{row.unit_name}</td>
										<td className="td-input" onClick={() => setData(prev => ({ ...prev, activeIndex: index }))}>
											<input
												type="text"
												className="form-control text-end"
												placeholder="0"
												value={row.quantity ?? ''}
												onChange={(e) => patchOutputQuantity(index, e.target.value)}
												onKeyDown={(e) => handleOutputRowKeyDown(e, index, 'quantity')} />
										</td>
										<td className="td-input" onClick={() => setData(prev => ({ ...prev, activeIndex: index }))}>
											<input
												type="text"
												className="form-control text-end"
												placeholder="0"
												value={row.cost_share_percent ?? ''}
												onChange={(e) => patchOutputSharePercent(index, e.target.value)}
												onKeyDown={(e) => handleOutputRowKeyDown(e, index, 'share')} />
										</td>
										<td className="td-input" onClick={() => setData(prev => ({ ...prev, activeIndex: index }))}>
											<input
												type="text"
												className="form-control text-end"
												placeholder="0"
												value={row.cost_price ?? ''}
												onChange={(e) => patchOutputCostPrice(index, e.target.value)}
												onKeyDown={(e) => handleOutputRowKeyDown(e, index, 'cost')} />
										</td>
										<td className="text-end text-nowrap" onClick={() => setData(prev => ({ ...prev, activeIndex: index }))}>
											{formatMoney(outputRowDisplayAmount(data.sourceRow, row))}
										</td>
										<td className="vertical-middle">
											<div className="d-flex justify-content-end">
												<div
													className="table-action-button table-action-danger-button"
													onClick={() => removeOutputRow(index)}>
													<i className="uil uil-times"></i>
												</div>
											</div>
										</td>
									</tr>
								))}
								<tr className="text-muted">
									<td></td>
									<td>{t('breakdown_outputs_remainder')}</td>
									<td></td>
									<td></td>
									<td></td>
									<td className="text-end text-nowrap text-danger">
										<b>{outputRemainderQty != null ? formatMoney(outputRemainderQty, 2) : '—'}</b>
									</td>
									<td className="text-end text-nowrap text-danger">
										<b>{outputRemainderShare != null ? `${formatMoney(outputRemainderShare, 2)}%` : '—'}</b>
									</td>
									<td></td>
									<td className="text-end text-nowrap text-danger">
										<b>{outputRemainderAmt != null ? formatMoney(outputRemainderAmt) : '—'}</b>
									</td>
									<td></td>
								</tr>
								<tr>
									<td className="td-input">
										<input
											type="text"
											className="form-control text-center"
											style={{ padding: '5px 0' }}
											placeholder="xxx"
											value={addQuantity}
											onChange={(e) => setAddQuantity(e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, ''))}
										/>
									</td>
									<td colSpan={1} style={{ padding: 0 }}>
										<div className="d-flex">
											<div className="w-100">
												<button
													type="button"
													className="btn-sm btn-primary w-100"
													disabled={!data.from_wms_id}
													onClick={addOutputRowsFromInput}>
													{t('add')}
												</button>
											</div>
										</div>
									</td>
									<td></td>
									<td></td>
									<td></td>
									<td className="text-end">
										<b>{formatMoney(sumOutputsQuantity(data.outputs), 2)}</b>
									</td>
									<td className="text-end text-nowrap">
										<b>{formatMoney(outputsShareSum, 2)}%</b>
									</td>
									<td></td>
									<td className="text-end text-nowrap">
										<b>{formatMoney(sumOutputsAmount(data.outputs, data.sourceRow))}</b>
									</td>
									<td></td>
								</tr>
							</tbody>
						</table>
					</div>

					<div className="btn-wrapper gap-2">
						<Link to="/documents-breakdown" className="btn btn-warning btn-rounded btn-wide" tabIndex={-1}>
							{t('cancel')}
						</Link>
						<button type="button" className="btn btn-primary btn-rounded btn-wide" tabIndex={-1}
							onClick={() => createUpdate()}>
							{t('confirm')}
						</button>
					</div>
				</div>
			</div>
		</>
	)
}

export default CreateUpdate
